import { memo, useMemo } from 'react';

import {
  type AssetCustomFieldValue,
  type BoardCustomFieldValue,
  CustomFieldTypeName,
  type CustomFieldValueListResponse,
} from '@air/api/types';
import { cn } from '@air/utils-tailwind';

import { CustomField } from '~/components/CustomField';
import { SelectChipRemoveButton } from '~/components/Select/components/SelectChipRemoveButton';
import { orderedSortedFilterFunc, Select, type SelectProps } from '~/components/Select/Select';
import { type DefaultChipType } from '~/components/Select/shared/types';
import { CUSTOM_FIELD_SELECT_COMPONENT } from '~/constants/testIDs';

export type CustomFieldSelectProps = {
  field: BoardCustomFieldValue | AssetCustomFieldValue;
  cfOptions: CustomFieldValueListResponse | undefined;
  className?: string;
} & Omit<SelectProps, 'options' | 'selectedOptions'>;

export const CustomFieldSelect = memo(({ field, cfOptions, className, ...rest }: CustomFieldSelectProps) => {
  const selectedOptions = useMemo<DefaultChipType[]>(() => {
    if (field.value) {
      return [
        {
          value: field.value.id,
          label: field.value.value,
          meta: {
            color: field.value.color.name,
          },
        },
      ];
    }
    if (field.values) {
      return field.values.map((fieldValue) => ({
        value: fieldValue.id,
        label: fieldValue.value,
        meta: {
          color: fieldValue.color.name,
        },
      }));
    }

    return [];
  }, [field]);

  const options = useMemo<DefaultChipType[]>(() => {
    if (cfOptions) {
      return cfOptions.data.map((fieldValue) => ({
        value: fieldValue.id,
        label: fieldValue.value,
        meta: {
          color: fieldValue.color.name,
        },
      }));
    } else {
      return [];
    }
  }, [cfOptions]);

  return (
    <Select
      {...rest}
      closeMenuOnSelect={field.type.title === CustomFieldTypeName.singleSelect}
      // biome-ignore lint/plugin: Cypress selects this select by the field name baked into the test id
      data-testid={`${CUSTOM_FIELD_SELECT_COMPONENT}-${field.name}`}
      filterFunc={orderedSortedFilterFunc}
      options={options}
      placeholder="None"
      selectedOptions={selectedOptions}
      isSearchable
      isSticky
      // biome-ignore lint/performance/noJsxPropsBind: the chip renderer closes over this field's option color and the remove callback
      chipRenderer={(chip, removeSelectedItem, meta) => (
        <CustomField
          className="mr-1 mb-1"
          field={{
            id: chip.value,
            color: {
              name: chip?.meta?.color,
            },
            value: chip.label,
          }}
          suffix={meta?.isOpen ? <SelectChipRemoveButton chip={chip} onClick={removeSelectedItem} /> : undefined}
        />
      )}
      // biome-ignore lint/performance/noJsxPropsBind: the list item renderer reads this field's option color from the select props
      listItemRenderer={(props) => (
        <div className="rounded-4 p-2 ads:hover:bg-hover-subtle hover:bg-gray-4">
          <CustomField
            field={{
              id: props.value,
              color: {
                name: props?.meta?.color,
              },
              value: props.label,
            }}
          />
        </div>
      )}
      className={cn('w-full [&_.placeholder]:text-14', className)}
      maxDropdownHeight={300}
      isSingleSelect={field.type.title === CustomFieldTypeName.singleSelect}
      isClearable
      hideClearWhenNotFocused
      size="large"
    />
  );
});

CustomFieldSelect.displayName = 'CustomFieldSelect';
